import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';


import { DashboardService } from '../../dashboard/services/dashboard.service';
import { DirectMessageService } from '../../direct-message/services/direct-message.service';
import { GamePeriodService } from '../../game/services/game-period.service';
import { GameService } from '../../game/services/game.service';
import { MissionService } from '../../mission/services/mission.service';
import { PlayerService } from '../../player/services/player.service';
import { ThemeService } from '../../shared/theme/services/theme.service';
import { BadgeService } from '../../target-challenge/badge/services/badge.service';
import { SkillService } from '../../target-challenge/skill/services/skill.service';
import { TranslationService } from '../../translation/services/translation.service';
import { PreloadStatus } from '../models/preload-status';
import { AudioService } from '../../audio/services/audio.service';
import { RoutingService } from './routing.service';

@Injectable()
export class PreloadService {

  public preloadStatus: BehaviorSubject<PreloadStatus>;
  public preloadComplete: BehaviorSubject<boolean>;
  public preloadCount: BehaviorSubject<number>;

  private preloadObservables: Observable<any>[];

  /**
   * Constructor
   *
   * @param {Router} router
   * @param {GameService} gameService
   * @param {GamePeriodService} gamePeriodService
   * @param {ThemeService} themeService
   * @param {TranslationService} translationService
   * @param {DashboardService} dashboardService
   * @param {PlayerService} playerService
   * @param {RoutingService} routingService
   * @param {MissionService} missionService
   * @param {SkillService} skillService
   * @param {BadgeService} badgeService
   * @param {DirectMessageService} directMessageService
   * @param {AudioService} audioService
   */
  public constructor(
    private router: Router,
    private gameService: GameService,
    private gamePeriodService: GamePeriodService,
    private themeService: ThemeService,
    private translationService: TranslationService,
    private dashboardService: DashboardService,
    private playerService: PlayerService,
    private routingService: RoutingService,
    private missionService: MissionService,
    private skillService: SkillService,
    private badgeService: BadgeService,
    private directMessageService: DirectMessageService,
    private audioService: AudioService,
  ) {
    this.preloadObservables = [];

    this.preloadCount = new BehaviorSubject<number>(this.preloadObservables.length);
    this.preloadStatus = new BehaviorSubject<PreloadStatus>(null);
    this.preloadComplete = new BehaviorSubject(false);
  }

  /**
   * Preloads various API calls and their results.
   *
   * @return void
   */
  public doApplicationPreload(): void {
    this.preloadStatus.next(
      new PreloadStatus(0, 'initiated'),
    );

    this.preloadObservables = [
      this.translationService.loadCurrentTranslation(),
      this.dashboardService.getActiveWidgets(),
      this.gamePeriodService.getGamePeriods(true),
      this.routingService.getActiveNavigationItems(),
      this.playerService.getActivePlayer(),
      this.gameService.getGameModules(),

      // this.audioService.getAudioSettingsFromProfile(),
    ];

    this.missionService.clearMissionData();
    this.addPageSpecificDataCalls();

    this.preloadCount.next(this.preloadObservables.length);

    const subscriptionCountInitial = this.preloadObservables.length;
    let subscriptionCountFinished = 0;

    forkJoin(
      this.preloadObservables.map(
        o => o.pipe(
          tap(() => {
            subscriptionCountFinished++;
            if (subscriptionCountFinished <= subscriptionCountInitial) {
              const percentageDone: number = (subscriptionCountFinished / subscriptionCountInitial) * 100;
              this.preloadStatus.next(
                new PreloadStatus(percentageDone, 'progress'),
              );
            }
          }),
        ),
      ),
    ).subscribe(() => {
      this.preloadStatus.next(new PreloadStatus(100, 'finished'));
    });
  }

  /**
   * Listen to the preloadComplete Subject
   *
   * @return {Observable<boolean>}
   */
  public getPreloadComplete(): Observable<boolean> {
    return this.preloadComplete.asObservable();
  }

  /**
   * Do the actual preload calls to fetch the data for the first time.
   */
  private addPageSpecificDataCalls() {
    if (this.router.url.includes('world')) {
      this.preloadObservables.push(
        this.missionService.getMissionOverviewData(),
      );
    }

    if (this.router.url.includes('badges')) {
      this.preloadObservables.push(
        this.badgeService.getDataItemsForType('badge'),
      );
    }

    if (this.router.url.includes('skills')) {
      this.preloadObservables.push(
        this.skillService.getDataItemsForType('skill'),
      );
    }

    if (this.router.url.includes('notes')) {
      this.preloadObservables.push(
        this.directMessageService.getList(),
      );
    }
  }
}
